import React, { useState, useEffect, useContext } from 'react';
import { IcheerContext } from '../Context/AppContext'; // Adjust path as needed
import { StickyNote, Users, LogOut, CheckCircle, XCircle, User, Tag, MapPin, Plus, Search, Filter, Globe, Settings, Lock, Building, UserCheck, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';

const AdminDashboardPage = () => {
  const { host, setSuperIsAdminLoggedIn } = useContext(IcheerContext);
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [filteredTeams, setFilteredTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [showWebsiteModal, setShowWebsiteModal] = useState(false);
  const [showAdminUpdateModal, setShowAdminUpdateModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [registrationTypeFilter, setRegistrationTypeFilter] = useState('all');
  const [discountData, setDiscountData] = useState({
    percentage: '',
    starts_at: new Date().toISOString().slice(0, 16),
    ends_at: '',
  });
  const [adminUserName, setAdminUserName] = useState("");
  const [formData, setFormData] = useState({
    Name: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    promocode: '',
    street_address: '',   // ← Renamed
    city: '',             // ← NEW
    state: '',            // ← NEW
    zip_code: '',         // ← NEW
    teamType: '',
    teamSize: '',
    registrationType: 'team', // Default to team
    brand_ambassador_code: ''  // ← NEW (optional, only for individual)
  });
  const [websiteDetails, setWebsiteDetails] = useState({
    url: '',
    logo_url: '',
    discount_quantity: '',
    discount_quantity_variety_pack: ''
  });
  const [websiteLoading, setWebsiteLoading] = useState(false);
  const [websiteError, setWebsiteError] = useState('');
  const [adminUpdateLoading, setAdminUpdateLoading] = useState(false);
  const [adminUpdateError, setAdminUpdateError] = useState('');
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountFormData, setDiscountFormData] = useState({
    apply_team_funds: false,
    discount_quantity: 0,
    discount_quantity_variety_pack: 0,
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);
  const teamsPerPage = 10;
  const registrationTypes = [
    { value: 'team', label: 'Team', icon: Users },
    { value: 'gym', label: 'Gym', icon: Building },
    { value: 'individual', label: 'Individual Cheerleader', icon: UserCheck }
  ];
  const teamTypeOptions = [
    'Elementary',
    'Middle',
    'High School',
    'College UCA',
    'College NCA',
    'All Star',
    'Recreational'
  ];
  const sizeOptions = [
    '0–50 Cheerleaders',
    '50–100 Cheerleaders',
    '100+ Cheerleaders'
  ];
  const [adminUpdateData, setAdminUpdateData] = useState({
    username: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    setAdminUserName(localStorage.getItem('adminUserName'))
  }, []);

  // Fetch all teams
  useEffect(() => {
    const fetchTeams = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${host}team/all`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Failed to fetch teams');
        }
        const data = await response.json();
        setTeams(data);
        setFilteredTeams(data);
        setMessage('');
      } catch (error) {
        setMessage(error.message || 'Failed to fetch teams');
        setMessageType('danger');
      }
      setLoading(false);
    };
    fetchTeams();
  }, [host]);

  // Fetch website details when modal opens
  useEffect(() => {
    if (!showWebsiteModal) return;
    const fetchWebsiteDetails = async () => {
      setWebsiteLoading(true);
      setWebsiteError('');
      try {
        const response = await fetch(`${host}website/details`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Failed to fetch website details');
        }
        const data = await response.json();
        setWebsiteDetails({
          url: data.url,
          logo_url: data.logo_url,
          discount_quantity: data.discount_quantity.toString(),
          discount_quantity_variety_pack: data.discount_quantity_variety_pack.toString()
        });
      } catch (error) {
        setWebsiteError(error.message || 'Failed to fetch website details');
      }
      setWebsiteLoading(false);
    };
    fetchWebsiteDetails();
  }, [host, showWebsiteModal]);

  // Filter teams based on search, status, and registration type
  useEffect(() => {
    let filtered = teams;
    if (searchTerm) {
      filtered = filtered.filter(team =>
        team.Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        team.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        team.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        team.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        team.Promocode?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(team => team.status === statusFilter);
    }
    if (registrationTypeFilter !== 'all') {
      filtered = filtered.filter(team => {
        const teamRegistrationType = team.registration_type || 'team';
        return teamRegistrationType === registrationTypeFilter;
      });
    }
    setFilteredTeams(filtered);
    setCurrentPage(1);
  }, [teams, searchTerm, statusFilter, registrationTypeFilter]);

  // Reset admin update form when modal opens/closes
  useEffect(() => {
    if (showAdminUpdateModal) {
      setAdminUpdateData({
        username: '',
        password: '',
        confirmPassword: ''
      });
      setAdminUpdateError('');
    }
  }, [showAdminUpdateModal]);

  // Reset form when create modal closes
  useEffect(() => {
    if (!showCreateTeamModal) {
      setFormData({
        Name: '',
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        promocode: '',
        street_address: '',
        city: '',
        state: '',
        zip_code: '',
        teamType: '',
        teamSize: '',
        registrationType: 'team',
        brand_ambassador_code: ''
      });
    }
  }, [showCreateTeamModal]);

  // Handle input change for create team form
  const handleInputChange = (e) => {
    let { name, value } = e.target;
    if (name === 'promocode') {
      value = value.replace(/\s+/g, '');
    }
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Add to handle input change for discount modal
  const handleDiscountFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setDiscountFormData({
      ...discountFormData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  // Handle website details input change
  const handleWebsiteInputChange = (e) => {
    setWebsiteDetails({
      ...websiteDetails,
      [e.target.name]: e.target.value,
    });
  };

  // Handle admin update input change
  const handleAdminUpdateInputChange = (e) => {
    setAdminUpdateData({
      ...adminUpdateData,
      [e.target.name]: e.target.value,
    });
  };

  // Create team API call
  const createTeam = async (teamData) => {
    const response = await fetch(`${host}team/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(teamData),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Registration failed');
    }
    return response.json();
  };

  // Handle admin update
  const handleUpdateAdmin = async () => {
    if (!adminUpdateData.username.trim().length > 0 && !adminUpdateData.password.trim()) {
      setAdminUpdateError('Please provide at least username or password to update');
      return;
    }
    if (adminUpdateData.password.length < 6 && adminUpdateData.confirmPassword.length < 6) {
      setAdminUpdateError('Passwords must be at least 6 characters long');
      return;
    }
    if (adminUpdateData.password && adminUpdateData.password !== adminUpdateData.confirmPassword) {
      setAdminUpdateError('Passwords do not match');
      return;
    }
    if (adminUpdateData.password && adminUpdateData.password.length < 6) {
      setAdminUpdateError('Password must be at least 6 characters long');
      return;
    }
    setAdminUpdateLoading(true);
    try {
      const payload = {};
      if (adminUpdateData.username.trim()) {
        payload.username = adminUpdateData.username.trim();
      }
      if (adminUpdateData.password.trim()) {
        payload.password = adminUpdateData.password.trim();
      }
      const response = await fetch(`${host}admin/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update admin credentials');
      }
      setAdminUpdateError('');
      setMessage('Admin credentials updated successfully');
      setMessageType('success');
      setShowAdminUpdateModal(false);
      if (payload.username) {
        localStorage.setItem('adminUsername', payload.username);
      }
    } catch (error) {
      setAdminUpdateError(error.message || 'Failed to update admin credentials');
    }
    setAdminUpdateLoading(false);
  };

  // Handle team creation with validation
  const handleCreateTeam = async () => {
    const baseRequiredFields = ['Name', 'firstName', 'lastName', 'email', 'password', 'promocode', 'street_address', 'city', 'state', 'zip_code'];
    let requiredFields = [...baseRequiredFields];
    if (formData.registrationType === 'team') {
      requiredFields.push('teamType', 'teamSize');
    } else if (formData.registrationType === 'gym') {
      requiredFields.push('teamSize');
    } else if (formData.registrationType === 'individual') {
      // brand_ambassador_code is optional
    }
    const missingFields = requiredFields.filter((field) => !formData[field]?.trim());
    if (missingFields.length > 0) {
      setMessage('Please fill in all required fields: ' + missingFields.join(', '));
      setMessageType('danger');
      return;
    }
    if (formData.promocode.length < 6) {
      setMessage('Referral code must be at least 6 characters long');
      setMessageType('danger');
      return;
    }
    setLoading(true);
    try {
      const teamData = {
        Name: formData.Name,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        password: formData.password,
        promocode: formData.promocode,
        street_address: formData.street_address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip_code,
        team_type: formData.teamType || 'N/A',
        team_size: formData.teamSize || 'N/A',
        registration_type: formData.registrationType,
        brand_ambassador_code: formData.brand_ambassador_code || null
      };
      const response = await createTeam(teamData);
      setTeams([...teams, {
        ...teamData,
        id: response.team_id,
        status: 'unverified',
        discount_percentage: null,
        Promocode: formData.promocode
      }]);
      setMessage(`${formData.registrationType.charAt(0).toUpperCase() + formData.registrationType.slice(1)} created successfully`);
      setMessageType('success');
      setShowCreateTeamModal(false);
    } catch (error) {
      setMessage(error.message || 'Failed to create account');
      setMessageType('danger');
    }
    setLoading(false);
  };

  // Handle discount form input changes
  const handleDiscountInputChange = (e) => {
    setDiscountData({
      ...discountData,
      [e.target.name]: e.target.value,
    });
  };

  // Update website details
  const handleUpdateWebsiteDetails = async () => {
    if (!websiteDetails.url.trim() || !websiteDetails.logo_url.trim()) {
      setWebsiteError('URL and Logo URL are required');
      return;
    }
    if (isNaN(websiteDetails.discount_quantity) || Number(websiteDetails.discount_quantity) < 0) {
      setWebsiteError('Discount quantity must be a non-negative number');
      return;
    }
    if (isNaN(websiteDetails.discount_quantity_variety_pack) || Number(websiteDetails.discount_quantity_variety_pack) < 0) {
      setWebsiteError('Variety pack discount quantity must be a non-negative number');
      return;
    }
    setWebsiteLoading(true);
    try {
      const payload = {
        url: websiteDetails.url.trim(),
        logo_url: websiteDetails.logo_url.trim(),
        discount_quantity: Number(websiteDetails.discount_quantity),
        discount_quantity_variety_pack: Number(websiteDetails.discount_quantity_variety_pack)
      };
      const response = await fetch(`${host}website/details`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminUsername')}`
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update website details');
      }
      setWebsiteError('');
      setMessage('Website details updated successfully');
      setMessageType('success');
      setShowWebsiteModal(false);
    } catch (error) {
      setWebsiteError(error.message || 'Failed to update website details');
    }
    setWebsiteLoading(false);
  };

  // Handle Verification
  const handleVerification = async (teamtoUpdate) => {
    setLoading(true);
    try {
      const statusPayload = {
        email: teamtoUpdate.email,
        status: teamtoUpdate.status !== 'verified' ? 'verified' : 'unverified',
        percentage: 0,
      };
      const statusResponse = await fetch(`${host}team/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(statusPayload),
      });
      if (!statusResponse.ok) {
        const error = await statusResponse.json();
        throw new Error(error.detail || 'Failed to update team status');
      }
      setTeams(
        teams.map((team) =>
          team.email === teamtoUpdate.email
            ? {
              ...team,
              status: teamtoUpdate.status !== 'verified' ? 'verified' : 'unverified',
            }
            : team
        )
      );
      const registrationType = teamtoUpdate.registration_type || 'team';
      setMessage(
        `${registrationType.charAt(0).toUpperCase() + registrationType.slice(1)} ${teamtoUpdate.Name} ${teamtoUpdate.status !== 'verified' ? 'verified' : 'unverified'
        } successfully`
      );
      setMessageType('success');
      if (teamtoUpdate.status === 'verified') {
        setTeamToDelete(teamtoUpdate);
        setShowDeleteModal(true);
      }
    } catch (error) {
      setMessage(
        `Failed to manage discount for ${teamtoUpdate.promocode || 'account'} or ${teamtoUpdate.status === 'verified' ? 'verify' : 'unverify'
        } account ${teamtoUpdate.email}. Please try again.`
      );
      setMessageType('danger');
    }
    setLoading(false);
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('isAdminLoggedIn');
    localStorage.removeItem('adminUsername');
    setSuperIsAdminLoggedIn(false);
    navigate('/admin-login');
  };

  // Helper functions for dynamic labels
  const getFieldLabel = (baseLabel) => {
    if (formData.registrationType === 'gym') {
      return baseLabel.replace('Team', 'Gym');
    }
    return baseLabel;
  };
  const getNamePlaceholder = () => {
    switch (formData.registrationType) {
      case 'team': return 'Enter team name';
      case 'gym': return 'Enter gym name';
      case 'individual': return 'Enter gym/team name';
      default: return 'Enter name';
    }
  };
  const getSizePlaceholder = () => {
    switch (formData.registrationType) {
      case 'team': return 'Select team size';
      case 'gym': return 'Select gym size';
      default: return 'Select size';
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredTeams.length / teamsPerPage);
  const indexOfLastTeam = currentPage * teamsPerPage;
  const indexOfFirstTeam = indexOfLastTeam - teamsPerPage;
  const currentTeams = filteredTeams.slice(indexOfFirstTeam, indexOfLastTeam);
  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // Get stats
  const stats = {
    total: teams.length,
    verified: teams.filter(team => team.status === 'verified').length,
    unverified: teams.filter(team => team.status === 'unverified').length,
  };

  // Add handle discount update function
  const handleUpdateDiscount = async () => {
    if (isNaN(discountFormData.discount_quantity) || Number(discountFormData.discount_quantity) < 0) {
      setMessage('Discount quantity must be a non-negative number');
      setMessageType('danger');
      return;
    }
    if (isNaN(discountFormData.discount_quantity_variety_pack) || Number(discountFormData.discount_quantity_variety_pack) < 0) {
      setMessage('Variety pack discount quantity must be a non-negative number');
      setMessageType('danger');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${host}team/update-discounts/${selectedTeam.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          apply_team_funds: discountFormData.apply_team_funds,
          discount_quantity: Number(discountFormData.discount_quantity),
          discount_quantity_variety_pack: Number(discountFormData.discount_quantity_variety_pack),
        }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update discounts');
      }
      setTeams(
        teams.map((team) =>
          team.id === selectedTeam.id
            ? {
              ...team,
              apply_team_funds: discountFormData.apply_team_funds,
              discount_quantity: Number(discountFormData.discount_quantity),
              discount_quantity_variety_pack: Number(discountFormData.discount_quantity_variety_pack),
            }
            : team
        )
      );
      setMessage('Fund Raised Amount updated successfully');
      setMessageType('success');
      setShowDiscountModal(false);
    } catch (error) {
      setMessage(error.message || 'Failed to update discounts');
      setMessageType('danger');
    }
    setLoading(false);
  };

  // Add handleDeleteTeam function
  const handleDeleteTeam = async (teamId) => {
    setLoading(true);
    try {
      const response = await fetch(`${host}team/${teamId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete team');
      }
      setTeams(teams.filter(team => team.id !== teamId));
      setFilteredTeams(filteredTeams.filter(team => team.id !== teamId));
      setMessage('Team deleted successfully');
      setMessageType('success');
      setShowDeleteModal(false);
      setTeamToDelete(null);
    } catch (error) {
      setMessage(error.message || 'Failed to delete team');
      setMessageType('danger');
    }
    setLoading(false);
  };

  const exportToCSV = () => {
    const headers = [
      'ID', 'Name', 'First Name', 'Last Name', 'Email',
      'Promocode', 'Old Referral Codes', 'Street Address', 'City', 'State', 'Zip Code',
      'Team Type', 'Team Size', 'Registration Type',
      'Status', 'Created At', 'Updated At'
    ];
    const rows = teams.map(team => [
      team.id || '',
      team.Name || '',
      team.first_name || '',
      team.last_name || '',
      team.email || '',
      team.Promocode || '',
      Array.isArray(team.old_referral_codes)
        ? team.old_referral_codes.filter(Boolean).join(', ')
        : '',
      team.street_address || '',
      team.city || '',
      team.state || '',
      team.zip_code || '',
      team.team_type || '',
      team.team_size || '',
      team.registration_type || '',
      team.status || '',
      team.created_at ? new Date(team.created_at).toLocaleString() : '',
      team.updated_at ? new Date(team.updated_at).toLocaleString() : ''
    ]);
    const escapeCSV = (value) => {
      if (value == null) return '';
      const str = String(value);
      return str.includes(',') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"`
        : str;
    };
    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...rows.map(row => row.map(escapeCSV).join(','))
    ].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `icheer_teams_export_${new Date().toISOString().slice(0, 10)}.csv`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-vh-100" style={{ background: 'linear-gradient(135deg, #dc59a0 0%, #b84493 100%)' }}>
      <div className="container py-4">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-center">
              <div className="d-flex align-items-center mb-3 mb-lg-0">
                <img
                  src="https://cdn.shopify.com/s/files/1/0661/5177/6421/files/Logo_app.png"
                  className="img-fluid me-3"
                  style={{ height: '50px' }}
                  alt="Logo"
                />
                <div>
                  <h1 className="text-white mb-0 h3 fw-bold">User Management</h1>
                  <p className="text-white-50 mb-0 small">Admin Dashboard</p>
                </div>
              </div>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-light fw-semibold d-flex align-items-center"
                  style={{ borderRadius: '12px' }}
                  onClick={() => exportToCSV()}
                >
                  <StickyNote size={18} className="me-2" />
                  <span className="d-none d-sm-inline">Export Teams</span>
                </button>
                <button
                  className="btn btn-light fw-semibold d-flex align-items-center"
                  onClick={() => setShowAdminUpdateModal(true)}
                  style={{ borderRadius: '12px' }}
                >
                  <Settings size={18} className="me-2" />
                  <span className="d-none d-sm-inline">Admin Settings</span>
                </button>
                <button
                  className="btn btn-light fw-semibold d-flex align-items-center"
                  onClick={() => setShowWebsiteModal(true)}
                  style={{ borderRadius: '12px' }}
                >
                  <Globe size={18} className="me-2" />
                  <span className="d-none d-sm-inline">Website Details</span>
                </button>
                <button
                  className="btn btn-light fw-semibold d-flex align-items-center"
                  onClick={() => setShowCreateTeamModal(true)}
                  style={{ borderRadius: '12px' }}
                >
                  <Plus size={18} className="me-2" />
                  <span className="d-none d-sm-inline">Create Account</span>
                </button>
                <button
                  className="btn btn-outline-light fw-semibold d-flex align-items-center"
                  onClick={handleLogout}
                  style={{ borderRadius: '12px' }}
                >
                  <LogOut size={18} className="me-2" />
                  <span className="d-none d-sm-inline">Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="row mb-4">
          <div className="col-md-4 mb-3">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
              <div className="card-body text-center">
                <div className="d-flex align-items-center justify-content-center mb-2">
                  <Users size={24} className="text-primary me-2" />
                  <h3 className="mb-0 fw-bold text-primary">{stats.total}</h3>
                </div>
                <p className="text-muted mb-0 small fw-semibold">Total Users</p>
              </div>
            </div>
          </div>
          <div className="col-md-4 mb-3">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
              <div className="card-body text-center">
                <div className="d-flex align-items-center justify-content-center mb-2">
                  <CheckCircle size={24} className="text-success me-2" />
                  <h3 className="mb-0 fw-bold text-success">{stats.verified}</h3>
                </div>
                <p className="text-muted mb-0 small fw-semibold">Verified Users</p>
              </div>
            </div>
          </div>
          <div className="col-md-4 mb-3">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
              <div className="card-body text-center">
                <div className="d-flex align-items-center justify-content-center mb-2">
                  <XCircle size={24} className="text-warning me-2" />
                  <h3 className="mb-0 fw-bold text-warning">{stats.unverified}</h3>
                </div>
                <p className="text-muted mb-0 small fw-semibold">Pending Users</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="row">
          <div className="col-12">
            <div className="card shadow-lg" style={{ borderRadius: '15px !important', overflow: 'hidden' }}>
              {/* Card Header with Search and Filters */}
              <div className="card-header bg-white border-0 py-4" style={{ borderRadius: '20px 20px 0 0' }}>
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <h4 className="mb-0 fw-bold text-dark">All Users</h4>
                    <p className="text-muted mb-0 small">Manage and monitor user registrations</p>
                  </div>
                  <div className="col-md-6">
                    <div className="row g-2">
                      <div className="col-md-6">
                        <div className="input-group">
                          <span className="input-group-text bg-light border-0">
                            <Search size={16} className="text-muted" />
                          </span>
                          <input
                            type="text"
                            className="form-control border-0 bg-light"
                            placeholder="Search users..."
                            value={searchTerm}
                            autoComplete="off"
                            onChange={(e) => {
                              if (e.target.value !== "icheer-cookies") {
                                setSearchTerm(e.target.value)
                              } else {
                                setSearchTerm("")
                              }
                            }}
                            style={{ borderRadius: '0 12px 12px 0' }}
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <select
                          className="form-select border-0 bg-light"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          style={{ borderRadius: '12px' }}
                        >
                          <option value="all">All Status</option>
                          <option value="verified">Verified</option>
                          <option value="unverified">Unverified</option>
                        </select>
                      </div>
                      <div className="col-md-3">
                        <select
                          className="form-select border-0 bg-light"
                          value={registrationTypeFilter}
                          onChange={(e) => setRegistrationTypeFilter(e.target.value)}
                          style={{ borderRadius: '12px' }}
                        >
                          <option value="all">All Types</option>
                          <option value="team">Teams</option>
                          <option value="gym">Gyms</option>
                          <option value="individual">Individuals</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-body py-4">
                {message && (
                  <div className={`alert alert-${messageType} border-0 mx-4 mt-4`} style={{ borderRadius: '12px' }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <span>{message}</span>
                      <button type="button" className="btn-close" onClick={() => setMessage('')}></button>
                    </div>
                  </div>
                )}
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted mt-2">Loading users...</p>
                  </div>
                ) : currentTeams.length === 0 ? (
                  <div className="text-center py-5">
                    <Users size={48} className="text-muted mb-3" />
                    <p className="text-muted mb-0">No users found</p>
                    {searchTerm && (
                      <p className="text-muted small">Try adjusting your search criteria</p>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Desktop Table View */}
                    <div className="d-block">
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead className="bg-light">
                            <tr>
                              <th className="border-0 py-3 fw-semibold">Name</th>
                              <th className="border-0 py-3 fw-semibold">Contact</th>
                              <th className="border-0 py-3 fw-semibold">Referral Code</th>
                              <th className="border-0 py-3 fw-semibold">Status</th>
                              <th className="border-0 py-3 fw-semibold">Type</th>
                              <th className="border-0 py-3 fw-semibold">Registration</th>
                              <th className="border-0 py-3 fw-semibold">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentTeams.map((team) => (
                              <tr key={team.id}>
                                <td className="py-3">
                                  <div>
                                    <div className="fw-semibold">{team.Name}</div>
                                    <div style={{ maxWidth: "200px" }} className="text-muted small">
                                      {team.street_address || ''}{team.city ? `, ${team.city}` : ''}{team.state ? `, ${team.state}` : ''}{team.zip_code ? ` ${team.zip_code}` : ''}
                                    </div>
                                  </div>
                                </td>
                                <td className="py-3">
                                  <div>
                                    <div className="fw-semibold">{team.first_name} {team.last_name}</div>
                                    <div className="text-muted small">{team.email}</div>
                                  </div>
                                </td>
                                <td className="py-3">
                                  <div className="d-flex flex-column gap-2">
                                    <span
                                      className="badge bg-primary text-white px-3 py-2"
                                      style={{ borderRadius: '8px', fontSize: '0.9rem', fontWeight: '600' }}
                                    >
                                      {team.Promocode || 'N/A'}
                                      {team.Promocode && <small className="ms-1 opacity-75">(current)</small>}
                                    </span>
                                    {team.old_referral_codes && team.old_referral_codes.length > 0 ? (
                                      <div
                                        className="border rounded p-2"
                                        style={{
                                          borderColor: '#dee2e6',
                                          backgroundColor: '#f8f9fa',
                                          fontSize: '0.875rem'
                                        }}
                                      >
                                        <div className="text-muted small fw-semibold mb-1 border-bottom pb-1" style={{ borderColor: '#ced4da' }}>
                                          Old Codes
                                        </div>
                                        <div className="d-flex flex-column gap-1 mt-2">
                                          {team.old_referral_codes
                                            .filter(code => code && code.trim())
                                            .map((code, index) => (
                                              <span
                                                key={index}
                                                className="badge bg-light text-dark px-3 py-1"
                                                style={{ borderRadius: '6px', fontSize: '0.8rem' }}
                                              >
                                                {code}
                                              </span>
                                            ))}
                                        </div>
                                      </div>
                                    ) : (
                                      team.Promocode ? null : (
                                        <span className="text-muted small">N/A</span>
                                      )
                                    )}
                                  </div>
                                </td>
                                <td className="py-3">
                                  <span
                                    className={`badge px-3 py-2 ${team.status === 'verified' ? 'bg-success' : 'bg-warning'
                                      }`}
                                    style={{ borderRadius: '8px' }}
                                  >
                                    {team.status}
                                  </span>
                                </td>
                                <td className="py-3">
                                  <div>
                                    <div className="fw-semibold small">{team.team_type || 'N/A'}</div>
                                    {team.team_size && <div className="text-muted small">{team.team_size}</div>}
                                  </div>
                                </td>
                                <td className="py-3">
                                  {team.brand_ambassador_code ? (
                                    <span className="badge bg-info text-white px-2 py-1" style={{ borderRadius: '6px', fontSize: '0.7rem' }}>
                                      {team.registration_type} <br></br>({team.brand_ambassador_code})
                                    </span>
                                  ) : (
                                    <span className="badge bg-info text-white px-2 py-1" style={{ borderRadius: '6px', fontSize: '0.7rem' }}>
                                      {(team.registration_type || 'team').charAt(0).toUpperCase() + (team.registration_type || 'team').slice(1)}
                                    </span>
                                  )}
                                </td>
                                <td className="py-3">
                                  <button
                                    className={`btn btn-sm ${team.status === 'verified' ? 'btn-outline-warning' : 'btn-outline-success'
                                      }`}
                                    onClick={() => handleVerification(team)}
                                    disabled={loading}
                                    style={{ borderRadius: '8px', marginRight: "4px" }}
                                  >
                                    {team.status === 'verified' ? (
                                      <>
                                        <XCircle size={16} className="me-1" />
                                        Unverify
                                      </>
                                    ) : (
                                      <>
                                        <CheckCircle size={16} className="me-1" />
                                        Verify
                                      </>
                                    )}
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => {
                                      setSelectedTeam(team);
                                      setDiscountFormData({
                                        apply_team_funds: team.apply_team_funds || false,
                                        discount_quantity: team.discount_quantity || 0,
                                        discount_quantity_variety_pack: team.discount_quantity_variety_pack || 0,
                                      });
                                      setShowDiscountModal(true);
                                    }}
                                    disabled={loading}
                                    style={{ borderRadius: '8px' }}
                                  >
                                    <Tag size={16} className="me-1" />
                                    Update Fund Raised Amount
                                  </button>
                                  {team.status === 'unverified' && (
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => {
                                        if (team.status === 'verified') {
                                          setMessage('Only unverified teams can be deleted');
                                          setMessageType('danger');
                                          return;
                                        }
                                        setTeamToDelete(team);
                                        setShowDeleteModal(true);
                                      }}
                                      disabled={loading}
                                      style={{ borderRadius: '8px', marginLeft: '4px' }}
                                    >
                                      <Trash2 size={16} className="me-1" />
                                      Delete
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="p-4 border-top bg-light" style={{ borderRadius: '0 0 20px 20px' }}>
                        <nav>
                          <ul className="pagination justify-content-center mb-0">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button
                                className="page-link border-0 bg-transparent"
                                onClick={() => paginate(currentPage - 1)}
                                disabled={currentPage === 1}
                                style={{ borderRadius: '8px' }}
                              >
                                Previous
                              </button>
                            </li>
                            {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
                              let pageNumber;
                              if (totalPages <= 5) {
                                pageNumber = index + 1;
                              } else if (currentPage <= 3) {
                                pageNumber = index + 1;
                              } else if (currentPage >= totalPages - 2) {
                                pageNumber = totalPages - 4 + index;
                              } else {
                                pageNumber = currentPage - 2 + index;
                              }
                              return (
                                <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                                  <button
                                    className="page-link border-0"
                                    onClick={() => paginate(pageNumber)}
                                    style={{ borderRadius: '8px', margin: '0 2px' }}
                                  >
                                    {pageNumber}
                                  </button>
                                </li>
                              );
                            })}
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                              <button
                                className="page-link border-0 bg-transparent"
                                onClick={() => paginate(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                style={{ borderRadius: '8px' }}
                              >
                                Next
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Create Team Modal */}
        <div className={`modal fade ${showCreateTeamModal ? 'show d-block' : ''}`} tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
              <div className="modal-header border-0 pb-0">
                <div>
                  <h5 className="modal-title fw-bold mb-0">Create New Account</h5>
                  <p className="text-muted small mb-0">Add a new user to the system</p>
                </div>
                <button type="button" className="btn-close" onClick={() => setShowCreateTeamModal(false)}></button>
              </div>
              <div className="modal-body px-4">
                <div className="row g-3">
                  {/* Registration Type Selection */}
                  <div className="col-12">
                    <label className="form-label fw-semibold mb-3">
                      <UserCheck size={16} className="me-2" />
                      Registration Type *
                    </label>
                    <div className="row g-2">
                      {registrationTypes.map((type) => {
                        const IconComponent = type.icon;
                        return (
                          <div key={type.value} className="col-md-4">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="registrationType"
                                id={type.value}
                                value={type.value}
                                checked={formData.registrationType === type.value}
                                onChange={handleInputChange}
                                style={{ transform: 'scale(1.1)' }}
                              />
                              <label
                                className="form-check-label w-100 p-2 border rounded"
                                htmlFor={type.value}
                                style={{
                                  cursor: 'pointer',
                                  backgroundColor: formData.registrationType === type.value ? '#f0f8ff' : '#f8f9fa',
                                  borderColor: formData.registrationType === type.value ? '#667eea' : '#dee2e6',
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <div className="d-flex align-items-center">
                                  <IconComponent size={18} className="me-2 text-primary" />
                                  <span className="fw-semibold small">{type.label}</span>
                                </div>
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className='col-12'>
                    {message && (
                      <div className={`alert alert-${messageType} border-0 mt-4`} style={{ borderRadius: '12px' }}>
                        <div className="d-flex align-items-center justify-content-between">
                          <span>{message}</span>
                          <button type="button" className="btn-close" onClick={() => setMessage('')}></button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="firstName" className="form-label fw-semibold">
                      <User size={16} className="me-2" />
                      First Name *
                    </label>
                    <input
                      type="text"
                      className="form-control border-0 shadow-sm"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder="Enter first name"
                      style={{
                        backgroundColor: '#f8f9fa',
                        borderRadius: '12px',
                        padding: '12px 16px',
                      }}
                    />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="lastName" className="form-label fw-semibold">
                      <User size={16} className="me-2" />
                      Last Name *
                    </label>
                    <input
                      type="text"
                      className="form-control border-0 shadow-sm"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder="Enter last name"
                      style={{
                        backgroundColor: '#f8f9fa',
                        borderRadius: '12px',
                        padding: '12px 16px',
                      }}
                    />
                  </div>
                  <div className="col-12">
                    <label htmlFor="Name" className="form-label fw-semibold">
                      {formData.registrationType === 'team' && <Users size={16} className="me-2" />}
                      {formData.registrationType === 'gym' && <Building size={16} className="me-2" />}
                      {formData.registrationType === 'individual' && <UserCheck size={16} className="me-2" />}
                      {formData.registrationType === 'team' && 'Team Name *'}
                      {formData.registrationType === 'gym' && 'Gym Name *'}
                      {formData.registrationType === 'individual' && 'Gym / Team Name *'}
                    </label>
                    <input
                      type="text"
                      className="form-control border-0 shadow-sm"
                      id="Name"
                      name="Name"
                      value={formData.Name}
                      onChange={handleInputChange}
                      placeholder={getNamePlaceholder()}
                      style={{
                        backgroundColor: '#f8f9fa',
                        borderRadius: '12px',
                        padding: '12px 16px',
                      }}
                    />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="email" className="form-label fw-semibold">
                      Email *
                    </label>
                    <input
                      type="email"
                      className="form-control border-0 shadow-sm"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter email address"
                      style={{
                        backgroundColor: '#f8f9fa',
                        borderRadius: '12px',
                        padding: '12px 16px',
                      }}
                    />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="password" className="form-label fw-semibold">
                      Password *
                    </label>
                    <input
                      type="password"
                      className="form-control border-0 shadow-sm"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Enter password"
                      style={{
                        backgroundColor: '#f8f9fa',
                        borderRadius: '12px',
                        padding: '12px 16px',
                      }}
                    />
                    <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                      Minimum 6 characters
                    </small>
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="promocode" className="form-label fw-semibold">
                      <Tag size={16} className="me-2" />
                      Referral Code *
                    </label>
                    <input
                      type="text"
                      className="form-control border-0 shadow-sm"
                      id="promocode"
                      name="promocode"
                      value={formData.promocode}
                      onChange={handleInputChange}
                      placeholder="Create your referral code"
                      style={{
                        backgroundColor: '#f8f9fa',
                        borderRadius: '12px',
                        padding: '12px 16px',
                      }}
                    />
                    <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                      Minimum 6 characters
                    </small>
                  </div>

                  {/* Address Fields */}
                  <div className="col-12">
                    <label htmlFor="street_address" className="form-label fw-semibold">
                      <MapPin size={16} className="me-2" />
                      Street Address *
                    </label>
                    <textarea
                      className="form-control border-0 shadow-sm"
                      id="street_address"
                      name="street_address"
                      value={formData.street_address}
                      onChange={handleInputChange}
                      placeholder="Enter street address"
                      rows="2"
                      style={{
                        backgroundColor: '#f8f9fa',
                        borderRadius: '12px',
                        padding: '12px 16px',
                        resize: 'vertical',
                      }}
                    />
                  </div>

                  <div className="row mt-3">
                    <div className="col-md-4">
                      <label htmlFor="city" className="form-label fw-semibold">
                        City *
                      </label>
                      <input
                        type="text"
                        className="form-control border-0 shadow-sm"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="City"
                        style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: '12px',
                          padding: '12px 16px',
                        }}
                      />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="state" className="form-label fw-semibold">
                        State *
                      </label>
                      <input
                        type="text"
                        className="form-control border-0 shadow-sm"
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        placeholder="State"
                        style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: '12px',
                          padding: '12px 16px',
                        }}
                      />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="zip_code" className="form-label fw-semibold">
                        Zip Code *
                      </label>
                      <input
                        type="text"
                        className="form-control border-0 shadow-sm"
                        id="zip_code"
                        name="zip_code"
                        value={formData.zip_code}
                        onChange={handleInputChange}
                        placeholder="Zip Code"
                        style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: '12px',
                          padding: '12px 16px',
                        }}
                      />
                    </div>
                  </div>

                  {/* Brand Ambassador Code - Only for Individual (Optional) */}
                  <div className="col-12">
                    <label htmlFor="brand_ambassador_code" className="form-label fw-semibold">
                      <Tag size={16} className="me-2" />
                      Brand Ambassador Code
                    </label>
                    <input
                      type="text"
                      className="form-control border-0 shadow-sm"
                      id="brand_ambassador_code"
                      name="brand_ambassador_code"
                      value={formData.brand_ambassador_code}
                      onChange={handleInputChange}
                      placeholder="Enter ambassador code (optional)"
                      style={{
                        backgroundColor: '#f8f9fa',
                        borderRadius: '12px',
                        padding: '12px 16px',
                      }}
                    />
                    <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                      Did one of our Brand Ambassador Captains help you sign up?
                    </small>
                  </div>


                  {/* Team Type - only for teams */}
                  {formData.registrationType === 'team' && (
                    <div className="col-md-6">
                      <label htmlFor="teamType" className="form-label fw-semibold">
                        <Users size={16} className="me-2" />
                        Team Type *
                      </label>
                      <select
                        className="form-control border-0 shadow-sm"
                        id="teamType"
                        name="teamType"
                        value={formData.teamType}
                        onChange={handleInputChange}
                        style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: '12px',
                          padding: '12px 16px',
                        }}
                      >
                        <option value="" disabled>
                          Select team type
                        </option>
                        {teamTypeOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {/* Size - for teams and gyms */}
                  {(formData.registrationType === 'team' || formData.registrationType === 'gym') && (
                    <div className="col-md-6">
                      <label htmlFor="teamSize" className="form-label fw-semibold">
                        {formData.registrationType === 'team' && <Users size={16} className="me-2" />}
                        {formData.registrationType === 'gym' && <Building size={16} className="me-2" />}
                        {getFieldLabel('Team Size')} *
                      </label>
                      <select
                        className="form-control border-0 shadow-sm"
                        id="teamSize"
                        name="teamSize"
                        value={formData.teamSize}
                        onChange={handleInputChange}
                        style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: '12px',
                          padding: '12px 16px',
                        }}
                      >
                        <option value="" disabled>{getSizePlaceholder()}</option>
                        {sizeOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button
                  type="button"
                  className="btn btn-light px-4"
                  onClick={() => setShowCreateTeamModal(false)}
                  style={{ borderRadius: '12px' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary px-4"
                  onClick={handleCreateTeam}
                  disabled={loading}
                  style={{ borderRadius: '12px' }}
                >
                  {loading ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus size={16} className="me-2" />
                      Create Account
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Website Details Modal */}
        <div className={`modal fade ${showWebsiteModal ? 'show d-block' : ''}`} tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
              <div className="modal-header border-0 pb-0">
                <div>
                  <h5 className="modal-title fw-bold mb-0">Website Details</h5>
                  <p className="text-muted small mb-0">View and update website settings</p>
                </div>
                <button type="button" className="btn-close" onClick={() => setShowWebsiteModal(false)}></button>
              </div>
              <div className="modal-body px-4">
                {websiteError && (
                  <div className="alert alert-danger border-0 mb-3" style={{ borderRadius: '12px' }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <span>{websiteError}</span>
                      <button type="button" className="btn-close" onClick={() => setWebsiteError('')}></button>
                    </div>
                  </div>
                )}
                {websiteLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted mt-2">Loading website details...</p>
                  </div>
                ) : (
                  <div className="row g-3">
                    <div className="col-12">
                      <label htmlFor="url" className="form-label fw-semibold">
                        <Globe size={16} className="me-2" />
                        Website URL *
                      </label>
                      <input
                        type="url"
                        className="form-control border-0 shadow-sm"
                        id="url"
                        name="url"
                        value={websiteDetails.url}
                        onChange={handleWebsiteInputChange}
                        placeholder="Enter website URL"
                        style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: '12px',
                          padding: '12px 16px',
                        }}
                      />
                    </div>
                    <div className="col-12">
                      <label htmlFor="logo_url" className="form-label fw-semibold">
                        <Globe size={16} className="me-2" />
                        Logo URL *
                      </label>
                      <input
                        type="url"
                        className="form-control border-0 shadow-sm"
                        id="logo_url"
                        name="logo_url"
                        value={websiteDetails.logo_url}
                        onChange={handleWebsiteInputChange}
                        placeholder="Enter logo URL"
                        style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: '12px',
                          padding: '12px 16px',
                        }}
                      />
                    </div>
                    <div className="col-12">
                      <label htmlFor="discount_quantity" className="form-label fw-semibold">
                        <Tag size={16} className="me-2" />
                        Fund Raised (per cookie box) *
                      </label>
                      <input
                        type="number"
                        className="form-control border-0 shadow-sm"
                        id="discount_quantity"
                        name="discount_quantity"
                        value={websiteDetails.discount_quantity}
                        onChange={handleWebsiteInputChange}
                        placeholder="Enter discount quantity"
                        min="0"
                        style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: '12px',
                          padding: '12px 16px',
                        }}
                      />
                    </div>
                    <div className="col-12">
                      <label htmlFor="discount_quantity_variety_pack" className="form-label fw-semibold">
                        <Tag size={16} className="me-2" />
                        Fund Raised (per variety pack) *
                      </label>
                      <input
                        type="number"
                        className="form-control border-0 shadow-sm"
                        id="discount_quantity_variety_pack"
                        name="discount_quantity_variety_pack"
                        value={websiteDetails.discount_quantity_variety_pack}
                        onChange={handleWebsiteInputChange}
                        placeholder="Enter variety pack discount quantity"
                        min="0"
                        style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: '12px',
                          padding: '12px 16px',
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer border-0 pt-0">
                <button
                  type="button"
                  className="btn btn-light px-4"
                  onClick={() => setShowWebsiteModal(false)}
                  style={{ borderRadius: '12px' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary px-4"
                  onClick={handleUpdateWebsiteDetails}
                  disabled={websiteLoading}
                  style={{ borderRadius: '12px' }}
                >
                  {websiteLoading ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} className="me-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Admin Update Modal */}
        <div className={`modal fade ${showAdminUpdateModal ? 'show d-block' : ''}`} tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
              <div className="modal-header border-0 pb-0">
                <div>
                  <h5 className="modal-title fw-bold mb-0">Admin Settings</h5>
                  <p className="text-muted small mb-0">Update your admin credentials</p>
                </div>
                <button type="button" className="btn-close" onClick={() => setShowAdminUpdateModal(false)}></button>
              </div>
              <div className="modal-body px-4">
                {adminUpdateError && (
                  <div className="alert alert-danger border-0 mb-3" style={{ borderRadius: '12px' }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <span>{adminUpdateError}</span>
                      <button type="button" className="btn-close" onClick={() => setAdminUpdateError('')}></button>
                    </div>
                  </div>
                )}
                <div className="row g-3">
                  <div className="col-12">
                    <label htmlFor="adminUsername" className="form-label fw-semibold">
                      <User size={16} className="me-2" />
                      New Username
                    </label>
                    <input
                      type="text"
                      className="form-control border-0 shadow-sm"
                      id="adminUsername"
                      name="username"
                      value={adminUserName}
                      onChange={(e) => {
                        setAdminUserName(e.target.value);
                        handleAdminUpdateInputChange(e);
                      }}
                      placeholder="Enter new username (leave empty to keep current)"
                      style={{
                        backgroundColor: '#f8f9fa',
                        borderRadius: '12px',
                        padding: '12px 16px',
                      }}
                    />
                  </div>
                  <div className="col-12">
                    <label htmlFor="adminPassword" className="form-label fw-semibold">
                      <Lock size={16} className="me-2" />
                      New Password
                    </label>
                    <input
                      type="password"
                      className="form-control border-0 shadow-sm"
                      id="adminPassword"
                      name="password"
                      value={adminUpdateData.password}
                      onChange={handleAdminUpdateInputChange}
                      placeholder="Enter new password "
                      style={{
                        backgroundColor: '#f8f9fa',
                        borderRadius: '12px',
                        padding: '12px 16px',
                      }}
                    />
                    <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                      Leave empty to keep current
                    </small>
                  </div>
                  {adminUpdateData.password && (
                    <div className="col-12">
                      <label htmlFor="confirmPassword" className="form-label fw-semibold">
                        <Lock size={16} className="me-2" />
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        className="form-control border-0 shadow-sm"
                        id="confirmPassword"
                        name="confirmPassword"
                        value={adminUpdateData.confirmPassword}
                        onChange={handleAdminUpdateInputChange}
                        placeholder="Confirm your new password"
                        style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: '12px',
                          padding: '12px 16px',
                        }}
                      />
                    </div>
                  )}
                </div>
                <div className="alert alert-info border-0 mt-3" style={{ borderRadius: '12px' }}>
                  <small>
                    <strong>Note:</strong> You can update either username, password, or both. Leave fields empty to keep current values.
                  </small>
                </div>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button
                  type="button"
                  className="btn btn-light px-4"
                  onClick={() => setShowAdminUpdateModal(false)}
                  style={{ borderRadius: '12px' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary px-4"
                  onClick={handleUpdateAdmin}
                  disabled={adminUpdateLoading}
                  style={{ borderRadius: '12px' }}
                >
                  {adminUpdateLoading ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Updating...
                    </>
                  ) : (
                    <>
                      <Settings size={16} className="me-2" />
                      Update Credentials
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Team Fund Raised Modal */}
        <div className={`modal fade ${showDiscountModal ? 'show d-block' : ''}`} tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
              <div className="modal-header border-0 pb-0">
                <div>
                  <h5 className="modal-title fw-bold mb-0">Update Fund Raised Amount</h5>
                  <p className="text-muted small mb-0">Update fund raised amount settings for {selectedTeam?.Name}</p>
                </div>
                <button type="button" className="btn-close" onClick={() => setShowDiscountModal(false)}></button>
              </div>
              <div className="modal-body px-4">
                {message && (
                  <div className={`alert alert-${messageType} border-0 mb-3`} style={{ borderRadius: '12px' }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <span>{message}</span>
                      <button type="button" className="btn-close" onClick={() => setMessage('')}></button>
                    </div>
                  </div>
                )}
                <div className="row g-3">
                  <div className="col-12">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="apply_team_funds"
                        name="apply_team_funds"
                        checked={discountFormData.apply_team_funds}
                        onChange={handleDiscountFormChange}
                        style={{ transform: 'scale(1.1)' }}
                      />
                      <label className="form-check-label fw-semibold" htmlFor="apply_team_funds">
                        Apply Team Funds
                      </label>
                    </div>
                  </div>
                  <div className="col-12">
                    <label htmlFor="discount_quantity" className="form-label fw-semibold">
                      <Tag size={16} className="me-2" />
                      Fund Raised (per cookie box) *
                    </label>
                    <input
                      type="number"
                      className="form-control border-0 shadow-sm"
                      id="discount_quantity"
                      name="discount_quantity"
                      value={discountFormData.discount_quantity}
                      onChange={handleDiscountFormChange}
                      placeholder="Enter discount quantity"
                      disabled={!discountFormData.apply_team_funds}
                      min="0"
                      style={{
                        backgroundColor: '#f8f9fa',
                        borderRadius: '12px',
                        padding: '12px 16px',
                      }}
                    />
                    {!discountFormData.apply_team_funds && (
                      <small className="text-muted d-block mt-1">
                        General Fund Raised Amount will be applied.
                      </small>
                    )}
                  </div>
                  <div className="col-12">
                    <label htmlFor="discount_quantity_variety_pack" className="form-label fw-semibold">
                      <

                        Tag size={16} className="me-2" />
                      Fund Raised (per variety pack) *
                    </label>
                    <input
                      type="number"
                      className="form-control border-0 shadow-sm"
                      id="discount_quantity_variety_pack"
                      name="discount_quantity_variety_pack"
                      value={discountFormData.discount_quantity_variety_pack}
                      onChange={handleDiscountFormChange}
                      placeholder="Enter variety pack discount quantity"
                      disabled={!discountFormData.apply_team_funds}
                      min="0"
                      style={{
                        backgroundColor: '#f8f9fa',
                        borderRadius: '12px',
                        padding: '12px 16px',
                      }}
                    />
                    {!discountFormData.apply_team_funds && (
                      <small className="text-muted d-block mt-1">
                        General Fund Raised Amount will be applied.
                      </small>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button
                  type="button"
                  className="btn btn-light px-4"
                  onClick={() => setShowDiscountModal(false)}
                  style={{ borderRadius: '12px' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary px-4"
                  onClick={handleUpdateDiscount}
                  disabled={loading}
                  style={{ borderRadius: '12px' }}
                >
                  {loading ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} className="me-2" />
                      Save Raised Amount
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Delete Team */}
        <div className={`modal fade ${showDeleteModal ? 'show d-block' : ''}`} tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold mb-0">Confirm Delete</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body px-4">
                <p className="mb-0">Do you want to delete the {teamToDelete?.registration_type || 'team'} <strong>{teamToDelete?.Name}</strong> completely?</p>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button
                  type="button"
                  className="btn btn-light px-4"
                  onClick={() => setShowDeleteModal(false)}
                  style={{ borderRadius: '12px' }}
                >
                  No
                </button>
                <button
                  type="button"
                  className="btn btn-danger px-4"
                  onClick={() => handleDeleteTeam(teamToDelete.id)}
                  disabled={loading}
                  style={{ borderRadius: '12px' }}
                >
                  {loading ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} className="me-2" />
                      Yes, Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .form-check{
          display:flex;
          gap:5px;
          align-items:center;
        }
        .form-check-input{
          margin-top:0px;
        }
      `}</style>
    </div>
  );
};

export default AdminDashboardPage;